(() => {
  if (window.DentalCanvasQuickContactLoaded) return;
  window.DentalCanvasQuickContactLoaded = true;

  const defaultCopy = {
    heading: 'Want this workflow in your practice?',
    text: 'Enter your email and we’ll show you the relevant Dental Canvas setup.',
    button: 'Show me',
    success: 'You’re subscribed — check your inbox for the next step on this workflow.'
  };

  const workflowCopy = {
    'office-communication': {
      heading: 'Stop paying your team to search for the doctor.',
      text: 'Get a free workflow teardown for your operatories. We’ll map the request → acceptance → ETA → arrival → resolution flow beside Open Dental. Email only to start—no migration, no patient-data upload, no generic software tour.',
      button: 'Map My Doctor-Request Flow',
      success: 'You’re subscribed — we’ll email you to map the doctor-request flow for your practice.'
    },
    'ar-clarity': {
      heading: 'Find the money hiding inside your A/R before another month closes.',
      text: 'Get a free A/R workflow teardown. We’ll map patient responsibility, insurance balances, credits, aging, and the owner of each next action. Email only to start—no migration, no patient-data upload, no generic software tour.',
      button: 'Break Down My A/R',
      success: 'You’re subscribed — we’ll email you to break down the A/R workflow and the work behind each total.'
    },
    'associate-pay': {
      heading: 'Calculate associate pay correctly before payroll—and defend every dollar.',
      text: 'Get a free compensation workflow teardown. We’ll map attributable collections, lab fees, contract percentage, guarantee, days worked, carryover, break-even, and final pay. Email only to start—no spreadsheet rebuild and no generic software tour.',
      button: 'Build My Pay Formula',
      success: 'You’re subscribed — we’ll email you to map an explainable associate-pay formula.'
    },
    'hygiene-bonus': {
      heading: 'Build a hygiene bonus that rewards production without creating payroll fights.',
      text: 'Get a free bonus-formula teardown. We’ll map hourly pay, actual hours, production threshold, overflow, and bonus percentage into math both sides can verify. Email only to start—no spreadsheet rebuild and no generic software tour.',
      button: 'Build My Bonus Formula',
      success: 'You’re subscribed — we’ll email you to map the hygiene-bonus formula from production to final bonus.'
    },
    'insurance-follow-up': {
      heading: 'Put your oldest, highest-dollar insurance claims at the top of tomorrow’s call list.',
      text: 'Get a free insurance follow-up teardown. We’ll map how claim age and dollars still owed become a prioritized carrier queue with the exact claims behind each total. Email only to start—no migration, no patient-data upload, no generic software tour.',
      button: 'Prioritize My Insurance Queue',
      success: 'You’re subscribed — we’ll email you to map the carrier and claim follow-up order.'
    },
    'collections-promises': {
      heading: 'Stop broken payment promises from aging into bad debt.',
      text: 'Get a free collections workflow teardown. We’ll map every promise to an amount, due date, owner, history, and automatic follow-up when payment does not arrive. Email only to start—no migration, no patient-data upload, no generic software tour.',
      button: 'Fix My Promise Follow-Up',
      success: 'You’re subscribed — we’ll email you to map a closed-loop payment-promise workflow.'
    },
    'payroll-review': {
      heading: 'Catch unapproved minutes before they become paid labor.',
      text: 'Get a free payroll workflow teardown. We’ll map early punches, timecard exceptions, approvals, corrections, and export into one fast review before payroll closes. Email only to start—no spreadsheet chase and no generic software tour.',
      button: 'Review My Payroll Workflow',
      success: 'You’re subscribed — we’ll email you to map the time-review workflow before payroll is finalized.'
    },
    'checkout-handoff': {
      heading: 'Stop losing treatment, care calls, and next visits between the chair and front desk.',
      text: 'Get a free checkout workflow teardown. We’ll map exactly what leaves the operatory, who owns it at checkout, how the front desk acknowledges it, and what closes the loop before the patient leaves. Email only to start—no migration, no patient-data upload, no generic software tour.',
      button: 'Fix My Checkout Handoff',
      success: 'You’re subscribed — we’ll email you to map the closed-loop checkout handoff.'
    }
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.analyticsProcessingAllowed !== 'function' || privacy.analyticsProcessingAllowed();
    } catch (_) {
      return true;
    }
  };

  const eventPayload = (element) => {
    const host = element.closest('dc-interactive-demo');
    const demo = host?.dataset.demo || '';
    return {
      demo,
      workflow_tag: demo ? `dc-workflow-${demo}` : '',
      section_id: host?.dataset.sectionId || '',
      page_path: window.location.pathname,
      source: 'interactive_demo',
      marketing_consent: true
    };
  };

  const publish = (eventName, element) => {
    const payload = eventPayload(element);

    if (analyticsAllowed() && window.Shopify?.analytics?.publish) {
      Promise.resolve(window.Shopify.analytics.publish(eventName, payload)).catch(() => {});
    }

    element.dispatchEvent(new CustomEvent('dental-canvas:quick-contact-event', {
      bubbles: true,
      detail: { event: eventName, ...payload }
    }));
  };

  const applyWorkflowCopy = (root = document) => {
    const contacts = [...(root.querySelectorAll?.('[data-dc-quick-contact]') || [])];
    if (root.matches?.('[data-dc-quick-contact]')) contacts.unshift(root);

    contacts.forEach((contact) => {
      const host = contact.closest('dc-interactive-demo');
      const copy = workflowCopy[host?.dataset.demo || ''];
      if (!copy) return;

      const heading = contact.querySelector('.dcid__quick-copy h3');
      const text = contact.querySelector('.dcid__quick-copy p');
      const button = contact.querySelector('.dcid__quick-submit');
      const success = contact.querySelector('.dcid__quick-success');

      if (heading?.textContent.trim() === defaultCopy.heading) heading.textContent = copy.heading;
      if (text?.textContent.trim() === defaultCopy.text) text.textContent = copy.text;
      if (button?.textContent.trim() === defaultCopy.button) button.textContent = copy.button;
      if (success?.textContent.trim() === defaultCopy.success) success.textContent = copy.success;
    });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.dcid__quick-form');
    if (!form || !form.checkValidity()) return;
    publish('dental_canvas:workflow_lead_opt_in_submitted', form);
  });

  const focusReturnedMessage = () => {
    const target = document.querySelector('.dcid__quick-contact:target');
    const success = target?.querySelector('[data-dc-contact-success]');
    const error = target?.querySelector('[data-dc-contact-error]');
    const message = success || error;
    if (message) message.focus({ preventScroll: true });

    if (success && target) {
      const host = target.closest('dc-interactive-demo');
      const storageKey = `dc-workflow-subscribed:${window.location.pathname}:${host?.dataset.sectionId || ''}`;
      let alreadyPublished = false;
      try {
        alreadyPublished = window.sessionStorage.getItem(storageKey) === 'true';
        if (!alreadyPublished) window.sessionStorage.setItem(storageKey, 'true');
      } catch (_) {}
      if (!alreadyPublished) publish('dental_canvas:workflow_lead_subscribed', target);
    }
  };

  const initialize = (root = document) => {
    applyWorkflowCopy(root);
    focusReturnedMessage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  }
})();
